import { /* lazy, Suspense, */ useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown, ChevronUp, ArrowUpRight } from 'lucide-react';
import { useEnrichedHomepageLineup } from '../hooks/useEnrichedHomepageLineup';
import { SPRINTFIT_COLLECTIONS_URL } from '../constants/storeUrls';
import { TrustBar } from '../components/TrustBar';
import { ProductVideoCarousel } from '../components/ProductVideoCarousel';
import { HeroCinematic } from '../components/HeroCinematic';
import { SignatureThermaCubeStory } from '../components/SignatureThermaCubeStory';
import { TestimonialsMarquee } from '../components/TestimonialsMarquee';

/* Finder (3D body map) — re-enable later:
const BodyMapSection = lazy(() =>
  import('../components/bodyMap/BodyMapSection').then((m) => ({ default: m.BodyMapSection }))
);
*/

const EASE = [0.22, 1, 0.36, 1];

function formatInr(n) {
  return `\u20B9${Math.round(n).toLocaleString('en-IN')}`;
}

function ProductPriceLine({ price, compareAt }) {
  if (price == null || !(price > 0)) {
    return <span className="text-stone-500 text-sm tabular-nums">View price on store</span>;
  }
  return (
    <div className="flex items-baseline gap-2">
      {compareAt != null && compareAt > 0 ? (
        <span className="text-stone-400 line-through text-sm tabular-nums">{formatInr(compareAt)}</span>
      ) : null}
      <span className="text-stone-900 text-lg font-medium tabular-nums">{formatInr(price)}</span>
    </div>
  );
}

/** Situation → copy; product CTAs resolve from live `lineup`. */
const RECOVERY_SCENES = [
  {
    title: 'After long runs & hard sessions',
    body: 'When knees feel beat-up or quads won’t switch off, pair joint-ling heat (KneeRevive) with percussion (ProTherma). Reach for ThermaCube when you want contrast without ice-tray chaos.',
    image:
      'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?q=80&w=1400&auto=format&fit=crop',
    imageAlt: 'Runner on a track',
    primaryId: 'kneerevive',
    secondaryId: 'thermacube',
  },
  {
    title: 'Shoulders · desk · overhead lifting',
    body: 'Desk rounds and push days show up in the rotator cuff and traps. ShoulderEase lines are built for exactly that—Flex Pro when you want stronger programmes and faster don/doff.',
    image:
      'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=1400&auto=format&fit=crop',
    imageAlt: 'Athlete training with kettlebells',
    primaryId: 'shoulderease',
    secondaryId: 'shoulderease-flex',
  },
  {
    title: 'Deep tissue & stubborn knots',
    body: 'For fascia work and knots that shrug off foam rolling, FasciaBlade blends heat, vibration, and microcurrent. ProTherma stays the heavy hitter for big muscle groups.',
    image:
      'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1400&auto=format&fit=crop',
    imageAlt: 'Gym floor and athlete recovery mindset',
    primaryId: 'fasciablade',
    secondaryId: 'protherm',
  },
  {
    title: 'Activation & “waking up” muscles',
    body: 'Travel days, deload weeks, and prehab blocks—PulsePad’s TENS/EMS profiles fit in a pocket and run discreet sessions anywhere.',
    image:
      'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=1400&auto=format&fit=crop',
    imageAlt: 'Athlete stretching outdoors',
    primaryId: 'pulsepad',
    secondaryId: 'thermacube',
  },
];

const FAQ_ITEMS = [
  {
    q: 'What is SprintFit, and what do you sell?',
    a: 'SprintFit is our India-based athlete recovery brand. We design and ship contrast therapy (ThermaCube), targeted heat wraps, percussion with thermal heads, TENS/EMS, and fascia tools—one catalogue on sprintfit.in with consistent support and warranty.',
  },
  {
    q: 'Do you ship across India?',
    a: 'Yes. We fulfil pan-India from our operations; delivery times and courier options appear at checkout for your PIN code.',
  },
  {
    q: 'How do I pick the right product?',
    a: 'Scroll the recovery guide on this page for situation-based picks, use the floating recovery assistant (bottom-right) with your own words, or read Science for protocols. Prices on cards track our live Shopify catalog.',
  },
  {
    q: 'Warranty, replacement, and damaged delivery?',
    a: 'SprintFit hardware includes a 1-year warranty on manufacturing defects and 7-day return where applicable—confirm on the product page and your invoice. Contact support with your order details for help. If the parcel arrives damaged, reach out with photos within 48 hours of delivery.',
  },
  {
    q: 'Is checkout secure? Where is my order handled?',
    a: 'All purchases go through our official Shopify store at sprintfit.in with standard encrypted checkout. You will get order confirmation and tracking from the same store.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Arjun S.',
    place: 'Bengaluru',
    product: 'ThermaCube',
    quote:
      'Finally stopped juggling ice packs after football. Twenty minutes cold, done—no drips on the floor.',
  },
  {
    name: 'Priya M.',
    place: 'Mumbai',
    product: 'KneeRevive Pro',
    quote: 'I use it while I answer emails. Quiet, no fuss—my knees actually feel less stiff the next morning.',
  },
  {
    name: 'Rohan K.',
    place: 'Hyderabad',
    product: 'ProTherma',
    quote: 'Heats up faster than I expected. Build feels solid; not the flimsy stuff I had before.',
  },
  {
    name: 'Ananya T.',
    place: 'Delhi NCR',
    product: 'FasciaBlade',
    quote:
      'Keeps my shoulders and mid-back from locking up between shifts. Heat plus vibration actually gets used instead of sitting in a drawer.',
  },
  {
    name: 'Vikram P.',
    place: 'Pune',
    product: 'ThermaCube',
    quote: 'Wife borrowed it for her shoulder; now we argue over who gets it first. Ordering a second.',
  },
  {
    name: 'Neha R.',
    place: 'Chennai',
    product: 'PulsePad',
    quote:
      'Simple UI, clear manual. For someone who hates gadgets, this pocket TENS unit I actually keep charged.',
  },
];

function findProduct(lineup, id) {
  return lineup.find((p) => p.id === id) ?? null;
}

export function Home() {
  const [openFaq, setOpenFaq] = useState(null);
  const reduceMotion = useReducedMotion();
  const t = (base) => (reduceMotion ? { duration: 0 } : { ...base });
  const { lineup } = useEnrichedHomepageLineup();

  return (
    <div className="premium-recovery-home text-stone-900 font-body antialiased selection:bg-[#2c5f54]/15 selection:text-stone-900">
      <HeroCinematic />

      {/* Primary product focus — horizontal rail */}
      <section
        id="product-rail"
        className="relative scroll-mt-24 border-b border-stone-200/90 bg-[#faf8f5] py-12 md:py-16 px-5 sm:px-8"
      >
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10"
            initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={t({ duration: 0.65, ease: EASE })}
          >
            <div className="max-w-2xl">
              <p className="text-[10px] font-mono uppercase tracking-[0.26em] text-[#2c5f54] mb-3">The SprintFit shelf</p>
              <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-[-0.02em] text-stone-900">
                Recovery tools athletes actually use
              </h2>
            </div>
            <Link
              to="/science"
              className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.16em] text-[#2c5f54] hover:text-[#1c1b19] transition-colors shrink-0"
            >
              Protocols &amp; science
              <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
            </Link>
          </motion.div>

          <div className="-mx-5 sm:-mx-8 px-5 sm:px-8 flex gap-4 md:gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin [scrollbar-color:rgba(44,95,84,0.35)_transparent]">
            {lineup.map((p, index) => (
              <motion.article
                key={p.id}
                initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.12 }}
                transition={t({ duration: 0.55, ease: EASE, delay: reduceMotion ? 0 : index * 0.04 })}
                className={`snap-start shrink-0 w-[min(78vw,280px)] sm:w-[260px] flex flex-col rounded-2xl border bg-white overflow-hidden shadow-sm transition-shadow duration-500 hover:shadow-lg ${
                  p.id === 'thermacube' ? 'border-[#2c5f54]/30 ring-1 ring-[#2c5f54]/10' : 'border-stone-200/90'
                }`}
              >
                <div className="aspect-square overflow-hidden bg-stone-100 relative">
                  {p.detailPath ? (
                    <Link
                      to={p.detailPath}
                      className="group/img block h-full w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2c5f54] focus-visible:ring-inset"
                      aria-label={`${p.name} — product details`}
                    >
                      <img
                        src={p.image}
                        alt=""
                        className="h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover/img:scale-[1.04]"
                        loading={index < 3 ? 'eager' : 'lazy'}
                      />
                    </Link>
                  ) : (
                    <a
                      href={p.shopUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/img block h-full w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2c5f54] focus-visible:ring-inset"
                      aria-label={`${p.name} — view on store (opens in new tab)`}
                    >
                      <img
                        src={p.image}
                        alt=""
                        className="h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover/img:scale-[1.04]"
                        loading={index < 3 ? 'eager' : 'lazy'}
                      />
                    </a>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-500 mb-1">{p.category}</p>
                  <h3 className="font-heading text-lg font-medium text-stone-900 tracking-tight mb-2">{p.name}</h3>
                  <p className="text-stone-600 text-xs leading-relaxed mb-4 flex-1 line-clamp-3">{p.blurb}</p>
                  <div className="mb-4">
                    <ProductPriceLine price={p.price} compareAt={p.compareAt} />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-auto">
                    <a
                      href={p.shopUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex flex-1 min-w-[6rem] items-center justify-center gap-1.5 px-3 py-2.5 bg-[#1c1b19] text-[#f5f3ef] text-[10px] font-semibold uppercase tracking-[0.14em] hover:bg-[#2c5f54] transition-colors"
                    >
                      Shop
                      <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
                    </a>
                    {p.detailPath ? (
                      <Link
                        to={p.detailPath}
                        className="inline-flex flex-1 min-w-[6rem] items-center justify-center px-3 py-2.5 border border-stone-300 text-stone-800 text-[10px] font-semibold uppercase tracking-[0.14em] hover:bg-stone-50 transition-colors"
                      >
                        Details
                      </Link>
                    ) : null}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-10 pt-10 border-t border-stone-200/80">
            <TrustBar variant="light" />
          </div>
        </div>
      </section>

      {/* Situation-based guide */}
      <section
        id="recovery-guide"
        className="scroll-mt-24 py-16 md:py-24 px-5 sm:px-8 bg-[#f3f1ec] border-b border-stone-200/80"
      >
        <div className="max-w-[1100px] mx-auto">
          <motion.div
            className="max-w-2xl mb-12 md:mb-16"
            initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={t({ duration: 0.65, ease: EASE })}
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.26em] text-stone-500 mb-3">How athletes choose</p>
            <h2 className="font-heading text-2xl md:text-[2.25rem] font-medium tracking-[-0.02em] text-stone-900 mb-4">
              Match the situation to the tool
            </h2>
            <p className="text-stone-600 text-sm md:text-base leading-relaxed">
              We built this shelf so you are not guessing. Use the guide, then grab the right SKU on the rail above—or ask the recovery assistant floating on your screen.
            </p>
          </motion.div>

          <div className="flex flex-col gap-12 md:gap-16">
            {RECOVERY_SCENES.map((scene, i) => {
              const primary = findProduct(lineup, scene.primaryId);
              const secondary = findProduct(lineup, scene.secondaryId);
              const flip = i % 2 === 1;
              return (
                <motion.div
                  key={scene.title}
                  initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={t({ duration: 0.65, ease: EASE, delay: reduceMotion ? 0 : i * 0.06 })}
                  className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center ${flip ? 'md:[&>*:first-child]:order-2' : ''}`}
                >
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-stone-200 shadow-lg ring-1 ring-black/5">
                    <img src={scene.image} alt={scene.imageAlt} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl md:text-2xl font-medium text-stone-900 tracking-tight mb-3">
                      {scene.title}
                    </h3>
                    <p className="text-stone-600 text-sm md:text-base leading-relaxed mb-6">{scene.body}</p>
                    <div className="flex flex-wrap gap-3">
                      {primary ? (
                        <a
                          href={primary.shopUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1c1b19] text-[#f5f3ef] text-[11px] font-semibold uppercase tracking-[0.14em] hover:bg-[#2c5f54] transition-colors"
                        >
                          Start with {primary.name}
                          <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
                        </a>
                      ) : null}
                      {secondary ? (
                        <a
                          href={secondary.shopUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2.5 border border-stone-400/80 text-stone-900 text-[11px] font-semibold uppercase tracking-[0.14em] hover:bg-white/80 transition-colors"
                        >
                          Also consider {secondary.name}
                        </a>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        id="films"
        className="relative py-16 md:py-24 px-5 sm:px-8 bg-[#0a0b0c] border-b border-white/[0.06]"
      >
        <div className="max-w-[1100px] mx-auto">
          <motion.div
            className="text-center mb-10 md:mb-12"
            initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={t({ duration: 0.65, ease: EASE })}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-teal-400/80 mb-3">Product films</p>
            <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-[-0.02em] text-white">
              See the hardware we ship
            </h2>
            <p className="mt-3 text-zinc-500 text-sm max-w-md mx-auto">
              Tap play on a film to watch it here (YouTube player embedded after your click). Pause carousel on hover.
            </p>
          </motion.div>
          <ProductVideoCarousel />
        </div>
      </section>

      <SignatureThermaCubeStory />

      {/*
      Finder (3D body map) — re-enable with Navbar "Finder" link when ready.
      <Suspense
        fallback={
          <section
            id="body-map"
            className="py-24 px-5 sm:px-8 bg-[#06080a] border-b border-white/[0.06] text-center"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">Loading 3D finder…</p>
          </section>
        }
      >
        <BodyMapSection />
      </Suspense>
      */}

      <section id="reviews" className="scroll-mt-24 py-20 md:py-28 px-5 sm:px-8 bg-[#faf8f5] border-b border-stone-200/80">
        <div className="max-w-[1280px] mx-auto">
          <motion.div
            initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={t({ duration: 0.7, ease: EASE })}
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.26em] text-stone-500 mb-4">In their words</p>
            <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-[-0.02em] text-stone-900 mb-10 max-w-xl">
              Notes from athletes on the shelf
            </h2>
            <p className="text-stone-600 text-sm max-w-lg mb-8">
              Scrolls on a loop; on desktop, hover the strip to pause and read.
            </p>
          </motion.div>
          <TestimonialsMarquee items={TESTIMONIALS} />
        </div>
      </section>

      <section id="faq" className="scroll-mt-24 py-20 md:py-28 px-5 sm:px-8 bg-[#f3f1ec]">
        <div className="max-w-xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={t({ duration: 0.65, ease: EASE })}
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.26em] text-stone-500 mb-3">Care</p>
            <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-[-0.02em] text-stone-900">
              Before you choose
            </h2>
          </motion.div>
          <div className="space-y-2">
            {FAQ_ITEMS.map((item, i) => (
              <motion.div
                key={item.q}
                initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={t({ duration: 0.5, ease: EASE, delay: reduceMotion ? 0 : i * 0.04 })}
                className="border border-stone-200/90 rounded-xl overflow-hidden bg-white shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-stone-50/80 transition-colors"
                >
                  <span className="text-sm text-stone-800 font-medium pr-4">{item.q}</span>
                  {openFaq === i ? (
                    <ChevronUp className="w-4 h-4 text-stone-500 shrink-0" aria-hidden />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-stone-400 shrink-0" aria-hidden />
                  )}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-stone-600 text-sm leading-relaxed border-t border-stone-100 pt-3">{item.a}</div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 px-5 sm:px-8 overflow-hidden bg-[#1a2623] text-[#e8e6e1] border-t border-white/5">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_80%_60%_at_20%_0%,rgba(201,169,98,0.08),transparent_50%)]" aria-hidden />
        <motion.div
          className="relative max-w-[1280px] mx-auto flex flex-col md:flex-row md:items-end md:justify-between gap-10"
          initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={t({ duration: 0.75, ease: EASE })}
        >
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#c9a962]/90 mb-4">SprintFit store</p>
            <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-[-0.02em] text-white mb-4">
              Continue on the shelf you trust
            </h2>
            <p className="text-stone-400 text-sm max-w-md leading-relaxed">
              Same checkout, same care team—pick up bundles, colours, and new arrivals on sprintfit.in.
            </p>
          </div>
          <a
            href={SPRINTFIT_COLLECTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-white/20 text-white text-xs font-semibold uppercase tracking-[0.2em] hover:bg-white hover:text-[#1a2623] transition-all duration-300 shrink-0"
          >
            Open catalog
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" aria-hidden />
          </a>
        </motion.div>
      </section>
    </div>
  );
}
